import type {PropertyValue} from '@github-ui/custom-properties-types'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {CopilotIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'

interface SuggestPropertyValueResult {
  value: PropertyValue | undefined
  explanation: string
}

interface CopilotSuggestValueButtonProps {
  propertyName: string
  suggestValuePath: string
  disabled?: boolean
  onSuggestStart: () => void
  onCopilotSuggestion: (value: PropertyValue | undefined, explanation: string) => void
}

export function CopilotSuggestValueButton({
  propertyName,
  suggestValuePath,
  disabled,
  onSuggestStart,
  onCopilotSuggestion,
}: CopilotSuggestValueButtonProps) {
  const {isFetching, refetch} = useQuery<SuggestPropertyValueResult>({
    queryKey: ['suggestPropertyValue', propertyName, suggestValuePath],
    queryFn: async () => {
      const response = await verifiedFetchJSON(suggestValuePath, {
        method: 'POST',
        body: {
          propertyName,
        },
      })

      const json = await response.json()

      if (response.ok) {
        return {
          value: json.suggestion.value,
          explanation: json.suggestion.explanation ?? '',
        }
      }

      return {
        value: undefined,
        explanation: json.error,
      }
    },
    enabled: false,
  })

  const handleSuggest = async () => {
    onSuggestStart()
    const {data} = await refetch()
    if (data) {
      onCopilotSuggestion(data.value, data.explanation)
    } else {
      onCopilotSuggestion(undefined, 'Something went wrong.')
    }
  }

  return (
    <IconButton
      icon={CopilotIcon}
      variant="invisible"
      size="small"
      onClick={handleSuggest}
      disabled={disabled || isFetching}
      aria-label={`Suggest value for ${propertyName} with Copilot`}
    />
  )
}
